import { useState } from "react";
import CrudModule from "../components/CrudModule";
import InvoicesQboSyncBar from "../components/InvoicesQboSyncBar";
import PaymentRemindersBar from "../components/PaymentRemindersBar";
import CustomerCreditBadge from "../components/CustomerCreditBadge";
import SendInvoiceModal from "../components/SendInvoiceModal";
import BulkSendInvoicesBar from "../components/BulkSendInvoicesBar";
import InvoiceAutofillBanner from "../components/InvoiceAutofillBanner";
import { API } from "../lib/api";
import { FilePdf, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS = [
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
];

/**
 * Download the branded PDF from the server (identical to what the customer
 * receives via email). Uses fetch + Blob so we can honor the JWT auth header
 * without leaking the URL, and forces a save-as via a synthetic anchor.
 */
async function downloadServerPDF(invoice) {
  try {
    const token = localStorage.getItem("truck_token");
    const res = await fetch(`${API}/invoices/${invoice.id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => "");
      toast.error(detail?.slice(0, 200) || "PDF download failed");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoice-${invoice.invoice_number || invoice.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Invoice PDF downloaded");
  } catch (err) {
    toast.error("PDF download failed");
  }
}

export default function InvoicesPage() {
  const [sendTarget, setSendTarget] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <>
      {/* Autofill banner — only renders when arriving from Ready-to-Invoice */}
      <InvoiceAutofillBanner onCreated={() => setReloadKey((k) => k + 1)} />
      <InvoicesQboSyncBar />
      <PaymentRemindersBar />
      <BulkSendInvoicesBar onSent={() => setReloadKey((k) => k + 1)} />
      <CrudModule
        key={reloadKey}
        testidPrefix="invoices"
        resource="invoices"
        overline="BILLING"
        title="Invoices & Reminders"
        subtitle="Generate branded PDF invoices, email them to your customers, and sync to QuickBooks in one click."
        aiCategory="invoice_reminder"
        aiContextFields={["invoice_number", "customer", "amount", "due_date", "status", "load_reference"]}
        primaryField="invoice_number"
        initialForm={{ status: "draft", amount: 0 }}
        extraRowActions={(row) => [
          {
            icon: <FilePdf size={16} weight="regular" />,
            onClick: () => downloadServerPDF(row),
            tooltip: "Download branded PDF",
            testid: `invoices-pdf-${row.id}`,
            color: "primary",
          },
          {
            icon: <PaperPlaneTilt size={16} weight="regular" />,
            onClick: () => setSendTarget(row),
            tooltip: "Email PDF to customer",
            testid: `invoices-send-${row.id}`,
            color: "info",
          },
        ]}
        fields={[
          { key: "invoice_number", label: "Invoice #", required: true, placeholder: "INV-2001" },
          { key: "customer", label: "Customer", required: true },
          { key: "customer_email", label: "Customer email", type: "email" },
          { key: "load_reference", label: "Load reference" },
          { key: "amount", label: "Amount ($)", type: "number", required: true },
          { key: "due_date", label: "Due date", type: "date", required: true },
          { key: "status", label: "Status", type: "select", options: STATUS },
          { key: "notes", label: "Notes", type: "textarea", span: 2 },
        ]}
        columns={[
          { key: "invoice_number", label: "INVOICE", render: (r) => <span className="font-mono text-xs">{r.invoice_number}</span> },
          { key: "customer", label: "CUSTOMER", render: (r) => (
            <div className="inline-flex items-center gap-2">
              <CustomerCreditBadge name={r.customer} />
              <span>{r.customer}</span>
            </div>
          ) },
          { key: "amount", label: "AMOUNT", render: (r) => <span className="font-mono">${Number(r.amount).toLocaleString()}</span> },
          { key: "due_date", label: "DUE" },
          { key: "status", label: "STATUS" },
        ]}
      />
      {sendTarget && (
        <SendInvoiceModal
          invoice={sendTarget}
          onClose={() => setSendTarget(null)}
          onSent={() => setReloadKey((k) => k + 1)}
        />
      )}
    </>
  );
}
